import { useAuth } from "../../context/AuthContext";
import { CalendarClock, Check, GripVertical, ListOrdered, Pause } from "lucide-react";
import { ActionMenu } from "../../components/ui/action-menu";
import { CopyButton } from "../../components/ui/copy-button";
import { cn } from "../../lib/utils";
import { getBatchCardTimestamp } from "./batch-time";
import { STATUS_META, type Batch } from "./types";

export function BatchCard({ b, onSelect, onMenu }: {
  b: Batch; onSelect: () => void; onMenu: (action: string) => void;
}) {
  const meta = STATUS_META[b.status];
  const pct = b.total ? Math.round((b.completed / b.total) * 100) : 0;
  const liveCallCount = Math.max(b.active_calls_count ?? 0, b.running);
  const { user } = useAuth() as any;
  const timestamp = getBatchCardTimestamp(b, user?.timezone);
  const actionItems = [
    { label: "View details", onClick: onSelect },
    ...(["running", "scheduled"].includes(b.status)
      ? [{ label: "Pause", onClick: () => onMenu("pause") }]
      : []),
    ...(b.status === "paused"
      ? [
          { label: "Resume", onClick: () => onMenu("resume") },
          { label: "Cancel remainder", danger: true, onClick: () => onMenu("cancel-remainder") },
        ]
      : []),
  ];

  const footer = {
    running:   <><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success" /> {liveCallCount} live call{liveCallCount === 1 ? "" : "s"}</>,
    paused:    <><Pause className="h-3 w-3 text-warning" /> Paused</>,
    queued:    <><ListOrdered className="h-3 w-3" /> In queue</>,
    scheduled: <><CalendarClock className="h-3 w-3 text-warning" /> Awaiting future attempt or callback</>,
    completed: <><Check className="h-3 w-3 text-success" /> Done</>,
    failed:    <><span className="text-danger">{b.error?.code ?? "Failed"}</span></>,
    cancelled: <>Cancelled</>,
  }[b.status];

  return (
    <div
      draggable
      onClick={onSelect}
      className="group relative cursor-grab rounded-xl shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524] p-3 pl-4 transition-all hover:-translate-y-0.5 hover:shadow-card-hover active:cursor-grabbing"
    >
      {/* status spine (Bitrix left rail) */}
      <span aria-hidden className="absolute bottom-3 left-0 top-3 w-[3px] rounded-r" style={{ background: meta.spine }} />

      {/* row 1: title + drag + menu */}
      <div className="flex items-start gap-1.5">
        <p className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">{b.name}</p>
        <GripVertical aria-hidden className="h-4 w-4 text-ink-3 opacity-0 transition-opacity group-hover:opacity-100" />
        <ActionMenu ariaLabel={`${b.name} actions`} items={actionItems} />
      </div>

      {/* row 2: agent name */}
      <div className="mt-0.5 flex items-center gap-1.5">
        <span className="min-w-0 flex-1 truncate text-[11px] font-semibold text-ink-2" title={b.agent}>{b.agent}</span>
      </div>

      {/* row 2b: schedule id */}
      <div className="mt-0.5 flex items-center gap-1 text-[10px] text-ink-3" title={b.schedule_id}>
        <span className="font-semibold">ID:</span>
        <span className="truncate font-mono">{b.schedule_id}</span>
        <CopyButton value={b.schedule_id} />
      </div>

      {/* progress */}
      <div className="mt-1.5">
        <div className="flex justify-between font-mono text-[10px] text-ink-3">
          <span>{b.completed.toLocaleString("en-IN")} / {b.total.toLocaleString("en-IN")}</span>
          <span>{pct}%</span>
        </div>
        <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-line">
          <div className={cn("h-full rounded-full", b.status === "running" && "animate-pulse")}
            style={{ width: `${pct}%`, background: meta.spine }} />
        </div>
      </div>

      {timestamp && (
        <div
          className="mt-1.5 flex min-w-0 items-center gap-1.5 text-[10px] text-ink-3"
          title={timestamp.title}
        >
          <CalendarClock className="h-3 w-3 shrink-0" />
          <span className="shrink-0 font-semibold">{timestamp.label}</span>
          <span className="truncate font-mono">{timestamp.value}</span>
        </div>
      )}

      {/* footer live line */}
      <div className="mt-1.5 flex items-center gap-1.5 text-[11px] font-medium text-ink-3">{footer}</div>
    </div>
  );
}
